import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  GridLegacy as Grid,
  Card,
  CardContent,
  Stack,
  Paper,
  Divider,
  Chip,
  useTheme,
  useMediaQuery,
  Fade,
  Grow
} from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import SpeedIcon from '@mui/icons-material/Speed';
import LanguageIcon from '@mui/icons-material/Language';
import SecurityIcon from '@mui/icons-material/Security';
import GroupIcon from '@mui/icons-material/Group';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ChatIcon from '@mui/icons-material/Chat';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { useRTL } from '../context/RTLContext';
import ReferralModal from '../components/ReferralModal/ReferralModal';
import { translations } from '../translations/home';
import { supportConfig } from '../config/support';

const Home: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { language, direction } = useRTL();
  const [referralModalOpen, setReferralModalOpen] = useState(false);
  const [showContent, setShowContent] = useState(false);

  const lang = language.split('-')[0] as keyof typeof translations;
  const t = translations[lang] || translations.en;

  React.useEffect(() => {
    setTimeout(() => setShowContent(true), 100);
  }, []);

  const handleWhatsAppClick = () => {
    const whatsappNumber = supportConfig.whatsapp.number;
    const messageKey = lang as keyof typeof supportConfig.whatsapp.defaultMessage;
    const message = encodeURIComponent(
      supportConfig.whatsapp.defaultMessage[messageKey] || 
      supportConfig.whatsapp.defaultMessage.en
    );
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
  };

  const handleMessengerClick = () => {
    window.open(`https://m.me/${supportConfig.messenger.pageId}`, '_blank');
  };

  const features = [
    {
      icon: <AssignmentTurnedInIcon sx={{ fontSize: 40 }} />,
      title: t.features.triage.title,
      description: t.features.triage.description,
      color: '#1976d2'
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 40 }} />,
      title: t.features.efficiency.title,
      description: t.features.efficiency.description,
      color: '#2e7d32'
    },
    {
      icon: <LanguageIcon sx={{ fontSize: 40 }} />,
      title: t.features.multilingual.title,
      description: t.features.multilingual.description,
      color: '#9c27b0'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      title: t.features.secure.title,
      description: t.features.secure.description,
      color: '#ed6c02'
    }
  ];

  const stats = [
    { number: '50%', label: t.stats.timeReduction },
    { number: '95%', label: t.stats.accuracy },
    { number: '24/7', label: t.stats.availability },
    { number: '10+', label: t.stats.languages }
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          pt: { xs: 8, md: 12 },
          pb: { xs: 10, md: 14 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("/pattern.svg") repeat',
            opacity: 0.1
          }
        }}
      >
        <Container maxWidth="lg">
          <Fade in={showContent} timeout={600}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              {/* Logo */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <img 
                  src="/favicon.svg" 
                  alt="Tibbbi Logo" 
                  style={{ width: 48, height: 48, marginRight: 16 }}
                />
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  Tibbbi
                </Typography>
              </Box>

              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  mb: 3,
                  lineHeight: 1.2
                }}
              >
                {t.hero.title}
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  opacity: 0.95,
                  fontWeight: 400,
                  fontSize: { xs: '1.1rem', md: '1.3rem' },
                  maxWidth: 800
                }}
              >
                {t.hero.subtitle}
              </Typography>

              {/* Support Buttons */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ mb: 4 }}
              >
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    backgroundColor: '#25D366',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#128C7E'
                    },
                    py: 1.5,
                    px: 3,
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none'
                  }}
                  startIcon={<WhatsAppIcon />}
                  onClick={handleWhatsAppClick}
                >
                  {t.hero.whatsapp}
                </Button>

                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    backgroundColor: '#0084FF',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#0066CC'
                    },
                    py: 1.5,
                    px: 3,
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none'
                  }}
                  startIcon={<ChatIcon />}
                  onClick={handleMessengerClick}
                >
                  {t.hero.messenger}
                </Button>
              </Stack>

              {/* Trust Indicators */}
              <Stack
                direction="row"
                spacing={3}
                sx={{
                  opacity: 0.9,
                  flexWrap: 'wrap',
                  gap: 2
                }}
              >
                <Chip
                  icon={<SecurityIcon />}
                  label={t.hero.badges.hipaa}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 600,
                    '& .MuiChip-icon': { color: 'white' }
                  }}
                />
                <Chip
                  icon={<GroupIcon />}
                  label={t.hero.badges.trusted}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 600,
                    '& .MuiChip-icon': { color: 'white' }
                  }}
                />
              </Stack>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ mt: -6, position: 'relative', zIndex: 2 }}>
        <Grow in={showContent} timeout={800}>
          <Grid container spacing={2}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    backgroundColor: 'white',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    borderRadius: 2,
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)'
                    }
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      color: '#1976d2',
                      fontWeight: 700,
                      mb: 1
                    }}
                  >
                    {stat.number}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Grow>
      </Container>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            mb: 2,
            color: '#1976d2'
          }}
        >
          {t.features.title}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            textAlign: 'center',
            mb: 6,
            color: 'text.secondary',
            fontWeight: 400
          }}
        >
          {t.features.subtitle}
        </Typography>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Fade in={showContent} timeout={1000 + index * 200}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    borderRadius: 2,
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: `${feature.color}15`,
                        mb: 3
                      }}
                    >
                      {React.cloneElement(feature.icon, {
                        sx: { fontSize: 40, color: feature.color }
                      })}
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Provider CTA Section */}
      <Box sx={{ backgroundColor: '#fff', py: 8 }}>
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
              p: { xs: 4, md: 6 },
              borderRadius: 3,
              textAlign: 'center',
              border: '2px solid',
              borderColor: 'primary.main',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <LocalHospitalIcon
              sx={{
                fontSize: 60,
                color: 'primary.main',
                mb: 3,
                opacity: 0.9
              }}
            />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: 'primary.main'
              }}
            >
              {t.providerCTA.title}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 4,
                color: 'text.secondary',
                fontWeight: 400
              }}
            >
              {t.providerCTA.subtitle}
            </Typography>

            <Stack spacing={3} sx={{ maxWidth: 500, mx: 'auto' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                onClick={() => setReferralModalOpen(true)}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 4px 20px rgba(25, 118, 210, 0.25)',
                  '&:hover': {
                    boxShadow: '0 6px 30px rgba(25, 118, 210, 0.35)'
                  }
                }}
              >
                {t.providerCTA.button}
              </Button>

              <Typography
                variant="body2"
                sx={{
                  color: 'success.main',
                  fontWeight: 600,
                  fontSize: '1rem'
                }}
              >
                {t.providerCTA.incentive}
              </Typography>
            </Stack>
          </Paper>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            mb: 6,
            color: '#1976d2'
          }}
        >
          {t.howItWorks.title}
        </Typography>

        <Grid container spacing={4}>
          {t.howItWorks.steps.map((step, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2rem',
                    fontWeight: 700,
                    mx: 'auto',
                    mb: 3
                  }}
                >
                  {index + 1}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  {step.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {step.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Footer */}
      <Box
        sx={{
          backgroundColor: '#1976d2',
          color: 'white',
          py: 4,
          mt: 8
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems="center"
            spacing={3}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img
                src="/favicon.svg"
                alt="Tibbbi Logo"
                style={{ width: 32, height: 32, marginRight: 12, filter: 'brightness(0) invert(1)' }}
              />
              <Typography variant="body1">
                {t.footer.copyright.replace('{year}', new Date().getFullYear().toString())}
              </Typography>
            </Box>
            <Stack direction="row" spacing={3}>
              <Button
                color="inherit"
                onClick={() => navigate('/questionnaire/new')}
                sx={{ textTransform: 'none' }}
              >
                {t.footer.startAssessment}
              </Button>
              <Button
                color="inherit"
                onClick={handleWhatsAppClick}
                sx={{ textTransform: 'none' }}
              >
                {t.footer.contact}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Referral Modal */}
      <ReferralModal
        open={referralModalOpen}
        onClose={() => setReferralModalOpen(false)}
      />
    </Box>
  );
};

export default Home;